import React from "react";
import { Plus, X, Save, Image } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CoOrganizersSection } from "./CoOrganizersSection";

export const OrganizerEventForm = ({
  editingEvent,
  setEditingEvent,
  eventForm,
  setEventForm,
  handleEventSubmit,
  setShowEventForm,
  handleImageUpload,
  eventImageRef,
  addTicketType,
  removeTicketType,
  updateTicketType
}) => {
  return (
    <form onSubmit={handleEventSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">
          {editingEvent ? "Modifier l'événement" : "Nouvel événement"}
        </h3>
        <Button type="button" variant="ghost" size="sm" onClick={() => {
          setShowEventForm(false);
          setEditingEvent(null);
        }}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Titre *</Label>
          <Input
            value={eventForm.title}
            onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
            placeholder="Nom de l'événement"
            className="mt-2"
            required
          />
        </div>
        <div>
          <Label>Artiste / DJ *</Label>
          <Input
            value={eventForm.artist}
            onChange={(e) => setEventForm({ ...eventForm, artist: e.target.value })}
            placeholder="Nom de l'artiste"
            className="mt-2"
            required
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Date *</Label>
          <Input
            value={eventForm.date}
            onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
            placeholder="Samedi 15 Mars 2025"
            className="mt-2"
            required
          />
        </div>
        <div>
          <Label>Horaire</Label>
          <Input
            value={eventForm.time}
            onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
            placeholder="22h00 - 05h00"
            className="mt-2"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Prix de base (€) *</Label>
          <Input
            type="number"
            value={eventForm.price}
            onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
            placeholder="35"
            className="mt-2"
            required
          />
        </div>
        <div>
          <Label>Billets disponibles *</Label>
          <Input
            type="number"
            value={eventForm.available_tickets}
            onChange={(e) => setEventForm({ ...eventForm, available_tickets: e.target.value })}
            placeholder="100"
            className="mt-2"
            required={!eventForm.useMultiTarif}
            min="0"
            disabled={eventForm.useMultiTarif}
          />
        </div>
        <div>
          <Label>Max billets / commande</Label>
          <Input
            type="number"
            value={eventForm.max_per_order || ""}
            onChange={(e) => setEventForm({ ...eventForm, max_per_order: e.target.value })}
            placeholder="10"
            className="mt-2"
            min="1"
            max="50"
          />
          <p className="text-xs text-muted-foreground mt-1">Nombre max de billets par achat</p>
        </div>
      </div>

      {/* Multi-tarif Toggle */}
      <div className="glass-card rounded-lg p-4 border border-primary/20">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium">Multi-tarifs</h4>
            <p className="text-xs text-muted-foreground">Proposer plusieurs types de billets</p>
          </div>
          <Button
            type="button"
            variant={eventForm.useMultiTarif ? "default" : "outline"}
            size="sm"
            onClick={() => setEventForm({ ...eventForm, useMultiTarif: !eventForm.useMultiTarif })}
          >
            {eventForm.useMultiTarif ? "Activé" : "Activer"}
          </Button>
        </div>

        {eventForm.useMultiTarif && (
          <div className="space-y-3 pt-3 border-t border-border">
            {eventForm.ticket_types.map((tt, index) => (
              <div key={index} className="glass-card rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Type #{index + 1}</span>
                  {eventForm.ticket_types.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeTicketType(index)}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Nom" value={tt.name} onChange={(e) => updateTicketType(index, 'name', e.target.value)} className="text-sm" required />
                  <Input type="number" placeholder="Prix €" value={tt.price} onChange={(e) => updateTicketType(index, 'price', e.target.value)} className="text-sm" required />
                  <Input type="number" placeholder="Qté" value={tt.quantity} onChange={(e) => updateTicketType(index, 'quantity', e.target.value)} className="text-sm" required min="1" />
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addTicketType} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un type
            </Button>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Lieu *</Label>
          <Input value={eventForm.venue} onChange={(e) => setEventForm({ ...eventForm, venue: e.target.value })} placeholder="Le Zenith Paris" className="mt-2" required />
        </div>
        <div>
          <Label>Adresse / Ville</Label>
          <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} placeholder="211 Avenue Jean Jaurès, 75019 Paris" className="mt-2" />
        </div>
        <div>
          <Label>Catégorie</Label>
          <Select value={eventForm.category} onValueChange={(v) => setEventForm({ ...eventForm, category: v })}>
            <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Hip-Hop/RNB">Hip-Hop/RNB</SelectItem>
              <SelectItem value="Afrobeat">Afrobeat</SelectItem>
              <SelectItem value="Dancehall/Caraïbes">Dancehall/Caraïbes</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Image de l'événement</Label>
        <p className="text-xs text-muted-foreground mt-1 mb-3">Format recommandé : portrait 1080x1350 px (ratio 4:5)</p>
        
        {/* Preview */}
        {eventForm.image && (
          <div className="relative mb-3 inline-block">
            <img src={eventForm.image} alt="" className="w-32 rounded-lg object-cover" style={{aspectRatio: '4/5'}} />
            <button type="button" onClick={() => setEventForm({ ...eventForm, image: "" })} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Upload button */}
        <input ref={eventImageRef} type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "event")} className="hidden" />
        <Button type="button" variant="outline" className="w-full py-6 border-dashed border-2" onClick={() => eventImageRef.current?.click()}>
          <Image className="w-5 h-5 mr-2" />
          {eventForm.image ? "Changer l'image" : "Choisir une image depuis votre téléphone"}
        </Button>
        
        {/* URL fallback */}
        <div className="mt-2">
          <Input value={eventForm.image} onChange={(e) => setEventForm({ ...eventForm, image: e.target.value })} placeholder="Ou coller une URL d'image ici..." className="text-xs" />
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Décrivez votre événement...&#10;&#10;Sautez des lignes pour créer des paragraphes." rows={6} className="mt-2" />
      </div>

      <div>
        <Label>Conditions du billet</Label>
        <Textarea value={eventForm.conditions || ""} onChange={(e) => setEventForm({ ...eventForm, conditions: e.target.value })} placeholder="Ex: Non remboursable&#10;L'organisateur se réserve le droit d'entrée&#10;Pièce d'identité obligatoire" rows={4} className="mt-2" />
      </div>

      {/* Co-organizers - only for existing events */}
      {editingEvent && editingEvent.id && (
        <CoOrganizersSection eventId={editingEvent.id} />
      )}

      <Button type="submit" className="w-full">
        <Save className="w-4 h-4 mr-2" />
        {editingEvent ? "Mettre à jour" : "Créer"}
      </Button>
    </form>
  );
};
